/**
 * src/components/TimeSeriesChart.js
 * Reusable time-series visualization — line, bar, or area charts via Recharts.
 */

import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend
} from 'recharts';

const COLORS = [
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#ec4899', // pink
  '#f97316', // orange
  '#14b8a6', // teal
  '#6366f1', // indigo
];

/** Add missing zero-count buckets between first and last data point per granularity */
function fillBuckets(allBuckets, granularity) {
  if (allBuckets.length === 0) return [];

  const sorted = [...allBuckets].sort();
  const start = new Date(sorted[0]);
  const end = new Date(sorted[sorted.length - 1]);
  const filled = [];
  const d = new Date(start);

  while (d <= end) {
    filled.push(d.toISOString());
    switch (granularity) {
      case 'hour': d.setUTCHours(d.getUTCHours() + 1); break;
      case 'day': d.setUTCDate(d.getUTCDate() + 1); break;
      case 'week': d.setUTCDate(d.getUTCDate() + 7); break;
      case 'month': d.setUTCMonth(d.getUTCMonth() + 1); break;
      default: d.setUTCDate(d.getUTCDate() + 1);
    }
  }
  return filled;
}

function formatBucketLabel(iso, granularity) {
  const d = new Date(iso);
  switch (granularity) {
    case 'hour':
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    case 'day':
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    case 'week':
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    case 'month':
      return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    default:
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
}

export default function TimeSeriesChart({ data, granularity, chartType }) {
  if (!data || data.length === 0) return null;

  // Collect all unique buckets across all series
  const allBuckets = new Set();
  for (const s of data) {
    for (const d of s.data) allBuckets.add(d.bucket);
  }
  const buckets = fillBuckets([...allBuckets], granularity);

  // Build lookup maps per series
  const seriesLookups = data.map(s => {
    const map = {};
    for (const d of s.data) map[d.bucket] = d.count;
    return map;
  });

  // Pivot into Recharts format
  const chartData = buckets.map(bucket => {
    const point = { bucket, label: formatBucketLabel(bucket, granularity) };
    data.forEach((s, i) => {
      point[`series_${i}`] = seriesLookups[i][bucket] || 0;
    });
    return point;
  });

  const seriesKeys = data.map((_, i) => `series_${i}`);
  const seriesNames = data.map(s => s.label);

  // Choose chart type
  const ChartComponent = chartType === 'bar' ? BarChart : chartType === 'area' ? AreaChart : LineChart;

  const renderSeries = () => {
    return seriesKeys.map((key, i) => {
      const color = COLORS[i % COLORS.length];
      if (chartType === 'bar') {
        return <Bar key={key} dataKey={key} name={seriesNames[i]} fill={color} />;
      }
      if (chartType === 'area') {
        return <Area key={key} type="monotone" dataKey={key} name={seriesNames[i]}
          stroke={color} fill={color} fillOpacity={0.15} strokeWidth={2} dot={false} />;
      }
      return <Line key={key} type="monotone" dataKey={key} name={seriesNames[i]}
        stroke={color} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />;
    });
  };

  // Reduce tick count for large datasets
  const maxTicks = 12;
  const tickInterval = chartData.length > maxTicks ? Math.ceil(chartData.length / maxTicks) - 1 : 0;

  return (
    <div className="analytics-timeseries">
      <ResponsiveContainer width="100%" height={280}>
        <ChartComponent data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={tickInterval} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border, #e5e7eb)' }}
            labelFormatter={(_, payload) => {
              if (payload?.[0]?.payload?.label) return payload[0].payload.label;
              return '';
            }}
          />
          {data.length > 1 && <Legend wrapperStyle={{ fontSize: '12px' }} />}
          {renderSeries()}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
}
